import type { ComponentProps, ComponentType } from "react";
import { z } from "zod";

import { MuxPlayer } from "./mux-player";

export const muxVideoSchema = z.object({
  playbackId: z.string(),
  aspectRatio: z
    .string()
    .regex(/^\d+:\d+$/)
    .nullable(),
  title: z.string(),
  autoplay: z.enum(["true", "muted"]).nullable(),
  loop: z.boolean().nullable(),
  thumbnailTime: z.number().nullable(),
  cssVars: z
    .object({
      playButton: z.enum(["none", "default"]).optional(),
      seekBackwardButton: z.enum(["none", "default"]).optional(),
      seekForwardButton: z.enum(["none", "default"]).optional(),
      fullscreenButton: z.enum(["none", "default"]).optional(),
      volumeRange: z.enum(["none", "default"]).optional(),
      timeRange: z.enum(["none", "default"]).optional(),
      timeDisplay: z.enum(["none", "default"]).optional(),
      durationDisplay: z.enum(["none", "default"]).optional(),
      renditionSelectmenu: z.enum(["none", "default"]).optional(),
      playbackRateButton: z.enum(["none", "default"]).optional(),
      liveButton: z.enum(["none", "default"]).optional(),
      airplayButton: z.enum(["none", "default"]).optional(),
      pipButton: z.enum(["none", "default"]).optional(),
    })
    .nullable(),
  className: z.string().optional(),
});

type MuxVideoProps = z.infer<typeof muxVideoSchema>;

type MuxPlayerComponent = ComponentType<ComponentProps<typeof MuxPlayer>>;

export function MuxVideo({
  aspectRatio,
  autoplay,
  loop,
  playbackId,
  title,
  cssVars,
  className,
  player: Player = MuxPlayer,
  ...props
}: Omit<ComponentProps<typeof MuxPlayer>, keyof MuxVideoProps> &
  MuxVideoProps & { player?: MuxPlayerComponent }) {
  const [width, height] = (aspectRatio ?? "16:9").split(":").map(Number) as [
    number,
    number,
  ];
  return (
    <Player
      {...props}
      playbackId={playbackId}
      title={title}
      metadata={{ video_title: title }}
      thumbnailTime={props.thumbnailTime ?? undefined}
      style={{
        aspectRatio: width / height,
        ...(cssVars
          ? {
              "--play-button":
                cssVars.playButton === "none" ? "none" : undefined,
              "--seek-backward-button":
                cssVars.seekBackwardButton === "none" ? "none" : undefined,
              "--seek-forward-button":
                cssVars.seekForwardButton === "none" ? "none" : undefined,
              "--fullscreen-button":
                cssVars.fullscreenButton === "none" ? "none" : undefined,
              "--volume-range":
                cssVars.volumeRange === "none" ? "none" : undefined,
              "--time-range": cssVars.timeRange === "none" ? "none" : undefined,
              "--time-display":
                cssVars.timeDisplay === "none" ? "none" : undefined,
              "--duration-display":
                cssVars.durationDisplay === "none" ? "none" : undefined,
              "--rendition-selectmenu":
                cssVars.renditionSelectmenu === "none" ? "none" : undefined,
              "--playback-rate-button":
                cssVars.playbackRateButton === "none" ? "none" : undefined,
              "--live-button":
                cssVars.liveButton === "none" ? "none" : undefined,
              "--airplay-button":
                cssVars.airplayButton === "none" ? "none" : undefined,
              "--pip-button": cssVars.pipButton === "none" ? "none" : undefined,
            }
          : {}),
      }}
      className={className}
      autoPlay={
        props.autoPlay ?? (autoplay === "muted" ? "muted" : autoplay === "true")
      }
      loop={loop ?? undefined}
    />
  );
}
